// Live agent-run activity context — kept separate from the provider component so
// the component file exports only components (react-refresh constraint), mirroring
// activity.ts / ActivityConnectionProvider.tsx.

import { createContext, useContext } from 'react';
import type { AgentActivityEvent } from '@shared/api-types';

export interface AgentActivityContextValue {
  /** Whether the SignalR connection is currently up. */
  connected: boolean;
  /**
   * Monotonic counter bumped on EVERY (re)connect. The action-required inbox
   * keys a reseed effect off this so it re-queries the DB-authoritative inbox on
   * each fresh connection — recovering any events missed while disconnected.
   * SignalR is a refresh trigger, never the delivery channel.
   */
  connectionEpoch: number;
  /**
   * Register a handler for live agent events. Returns an unsubscribe fn — call it
   * on unmount so a page only reacts while mounted. The copilot uses this to
   * stream progress; the inbox uses it ONLY to decide when to re-query.
   */
  onAgentActivity: (handler: (e: AgentActivityEvent) => void) => () => void;
}

export const AgentActivityContext = createContext<AgentActivityContextValue | null>(null);

/**
 * Access the live agent connection. Returns null when used outside the provider
 * (e.g. an unauthenticated route) so callers can no-op gracefully.
 */
export function useAgentActivity(): AgentActivityContextValue | null {
  return useContext(AgentActivityContext);
}
